import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import translations from "@/lib/i18n";
import API from "@/lib/api";

const LanguageContext = createContext();

const SUPPORTED_LANGS = ["nl", "en", "de", "fr", "es", "ar"];

function getNavigatorLang() {
  const nav = navigator.language || navigator.userLanguage || "";
  const short = nav.split("-")[0].toLowerCase();
  return SUPPORTED_LANGS.includes(short) ? short : null;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("amrav_lang");
    if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
    return "nl";
  });
  const [detectionMethod, setDetectionMethod] = useState("stored");
  const hasAutoDetected = useRef(!!localStorage.getItem("amrav_lang"));

  // Auto-detect language on first visit
  useEffect(() => {
    if (hasAutoDetected.current) return;
    hasAutoDetected.current = true;

    const detect = async () => {
      let detected = null;
      let method = "default";

      // 1. Try navigator.language (instant)
      const navLang = getNavigatorLang();

      // 2. Try geo-IP API (async)
      try {
        const { data } = await API.get("/geo-lang");
        if (data.language && SUPPORTED_LANGS.includes(data.language)) {
          detected = data.language;
          method = data.method || "geo_ip";
        }
      } catch {
        // Fallback to navigator
      }

      // Use geo-IP result if available, else navigator
      if (!detected && navLang) {
        detected = navLang;
        method = "navigator";
      }

      if (detected && detected !== lang) {
        setLang(detected);
        setDetectionMethod(method);
        localStorage.setItem("amrav_lang", detected);
        localStorage.setItem("amrav_lang_method", method);
      } else {
        localStorage.setItem("amrav_lang", lang);
        localStorage.setItem("amrav_lang_method", method);
      }

      // Track pageview
      try {
        await API.post("/analytics/pageview", {
          page: window.location.pathname,
          language: detected || lang,
          detection_method: method,
          referrer: document.referrer,
        });
      } catch {}
    };

    detect();
  }, []);

  // RTL support for Arabic
  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key) => {
      const keys = key.split(".");
      let result = translations[lang];
      for (const k of keys) {
        result = result?.[k];
      }
      return result || key;
    },
    [lang]
  );

  const setLangAndStore = useCallback((newLang) => {
    setLang(newLang);
    setDetectionMethod("manual");
    localStorage.setItem("amrav_lang", newLang);
    localStorage.setItem("amrav_lang_method", "manual");
    // Track the manual language switch
    API.post("/analytics/pageview", {
      page: window.location.pathname,
      language: newLang,
      detection_method: "manual_switch",
      referrer: "",
    }).catch(() => {});
  }, []);

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const idx = SUPPORTED_LANGS.indexOf(prev);
      const next = SUPPORTED_LANGS[(idx + 1) % SUPPORTED_LANGS.length];
      localStorage.setItem("amrav_lang", next);
      localStorage.setItem("amrav_lang_method", "manual");
      return next;
    });
  }, []);

  // Track conversions helper
  const trackConversion = useCallback(async (eventType, data = {}) => {
    try {
      await API.post("/analytics/conversion", {
        event_type: eventType,
        language: lang,
        detection_method: localStorage.getItem("amrav_lang_method") || "unknown",
        ...data,
      });
    } catch {}
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang: setLangAndStore, t, toggleLang, detectionMethod, trackConversion }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}
